import { Popup as PopupOld } from '@approvalmax/ui';
import { ConditionWrapper, Grid, Popup } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { useIsOcrUploadButtonVisible } from 'pages/requestForm/hooks/useIsOcrUploadButtonVisible';
import { getCanCreateContact } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import { CreateNetSuiteVendor } from 'shared/data/webApp/v1';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteBillFieldSettings,
    useNetSuiteExpensesFieldSettings,
    useNetSuiteItemsFieldSettings,
    useUserBetaFeatures,
} from 'shared/hooks';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { useOcrStatus } from '../../../hooks/useOcrStatus';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import { customGetRequestTransferState } from '../../../states';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import { OcrUploadButton } from '../../xero/OcrUploadButton/OcrUploadButton';
import {
    NetSuiteRequestFooterLegacy,
    NetSuiteRequestItemsSummary,
    NetSuiteVendorCreationPopupContent,
} from '../components';
import NetSuiteExpenses from '../components/NetSuiteExpenses/NetSuiteExpenses';
import NetSuiteItems from '../components/NetSuiteItems/NetSuiteItems';
import NetSuiteVendorCreationPopup from '../components/NetSuiteVendorCreationPopup/NetSuiteVendorCreationPopup';
import { convertCustomFieldsToState, getCustomFieldsTransfer, mapAddress } from '../helpers';
import {
    useCalculatedAmount,
    useCustomFields,
    useDiscountAmountValue,
    useDiscountDateValue,
    useExchangeRates,
    useExpenseItems,
    useItemLines,
    useResetBillFormState,
    useUploadAttachments,
} from '../hooks';
import {
    getExpenseAndItemLineTransfers,
    isFieldMandatory,
    isFieldVisible,
    isFutureDate,
} from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { Content, ContentInner, Root, Toolbar, ToolbarText } from '../NetSuiteRequestForm/NetSuiteRequestForm.styles';
import {
    netSuiteCustomFieldsState,
    netSuiteExpenseItemsState,
    netSuiteItemLineNumberState,
    netSuiteItemLinesState,
    netSuiteShowErrorsState,
} from '../resources/states';
import NetSuiteBillFieldsSection from './components/NetSuiteBillFieldsSection/NetSuiteBillFieldsSection';
import { getBillValidationErrors } from './NetSuiteBillRequestForm.helpers';
import { useNetSuiteBillFields } from './NetSuiteBillRequestForm.hooks';
import { messages } from './NetSuiteBillRequestForm.messages';
import { netSuiteBillFieldValuesState, updateNetSuiteBillDataState } from './NetSuiteBillRequestForm.states';
import { NetSuiteBillRequestFormProps } from './NetSuiteBillRequestForm.types';

export const NetSuiteBillRequestForm: FC<NetSuiteBillRequestFormProps> = memo((props) => {
    const { request } = props;

    const shouldPrefillEditFormRef = useRef<boolean>(true);

    const pageContext = useSelector(getContext);
    const editingContext = useSelector(getNetSuiteContext);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const expenseItems = useAtomValue(netSuiteExpenseItemsState);
    const itemLines = useAtomValue(netSuiteItemLinesState);
    const setNextLineNumber = useSetAtom(netSuiteItemLineNumberState);
    const setShowErrors = useSetAtom(netSuiteShowErrorsState);

    const fieldValues = useAtomValue(netSuiteBillFieldValuesState);

    const resetBillFormState = useResetBillFormState();

    const { setExpenseItems, updateExpenseItemsOnCurrencyChange, updateExpenseItemsAccount } = useExpenseItems(
        fieldValues.currency ?? request.details.currency
    );

    const { setItemLines, updateItemsOnCurrencyChange } = useItemLines(
        fieldValues.currency ?? request.details.currency
    );

    const {
        currencies,
        terms,
        items: editingContextItems,
        expenseCategories,
        availableLineItemType,
        customFields,
        documentFields,
    } = editingContext;

    const { updateFieldsBulk } = useNetSuiteBillFields();

    const { setCustomFieldValuesBulk } = useCustomFields(customFields);

    const { onUploadAttachments } = useUploadAttachments(request.id);

    const { totalAmount, netAmount, taxAmount } = useCalculatedAmount(fieldValues.currency);

    const fieldSettings = useNetSuiteBillFieldSettings(documentFields);
    const expensesFieldSettings = useNetSuiteExpensesFieldSettings(documentFields);
    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);
    const itemsFieldSettings = useNetSuiteItemsFieldSettings(documentFields);
    const setCustomGetRequestTransfer = useSetAtom(customGetRequestTransferState);
    const setUpdateNetSuiteBillData = useSetAtom(updateNetSuiteBillDataState);

    const discountAmount = useDiscountAmountValue(terms, fieldValues.terms?.id, netAmount, fieldValues.currency);

    const discountDate = useDiscountDateValue(terms, fieldValues.terms?.id, fieldValues.date);

    const { data: exchangeRatesData } = useExchangeRates(request.companyId, fieldValues.date);

    const [isVendorPopupOpened, setIsVendorPopupOpened] = useState(false);

    const defaultCurrencyRate = useMemo(() => {
        const exchangeRate = exchangeRatesData?.rates.find((currency) => {
            return currency.id === fieldValues.currency?.id;
        });

        return exchangeRate?.rateValue;
    }, [exchangeRatesData, fieldValues.currency]);

    useOcrStatus(request);

    const updateRequestData = useCallback(
        (request: domain.NetSuiteBillRequest) => {
            const { itemLines, expenseLines, ...fields } = request.details;

            const date = fields.transactionDate || moment().utc().startOf('day').toISOString();

            const term = terms.find((term) => term.id === fields.terms?.id);

            const computedDueDate = moment(date)
                .add(term?.daysUntilNetDue ?? 0, 'd')
                .toISOString();

            updateFieldsBulk(
                {
                    ...fields,
                    vendor: fields.vendor || undefined,
                    date,
                    dueDate:
                        fieldSettings.dueDate === domain.DocumentFieldState.Hidden
                            ? null
                            : fields.dueDate || computedDueDate,
                    address: mapAddress(fields.address),
                    exchangeRate:
                        request.exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
                },
                false
            );

            if (itemLines.length > 0 || expenseLines.length > 0) {
                const maxLineNumber = Math.max(
                    ...[
                        ...itemLines.map((item) => item.lineNumber),
                        ...expenseLines.map((expense) => expense.lineNumber),
                    ]
                );

                setNextLineNumber(maxLineNumber + 1);
            }

            const expandedExpenseLines = expenseLines.map((expenseLine) => {
                const expenseCategory = expenseCategories.find((category) => category.id === expenseLine.category?.id);

                if (expenseCategory) {
                    return {
                        ...expenseLine,
                        rates: expenseCategory.rates,
                    };
                }

                return expenseLine;
            });

            const expandedItemLines = itemLines.map((itemLine) => {
                const itemInfo = editingContextItems.find(
                    (editingContextItem) => editingContextItem.id === itemLine.item?.id
                );

                if (itemInfo) {
                    return {
                        ...itemLine,
                        unitList: itemInfo.unitList || [],
                    };
                }

                return itemLine;
            });

            setExpenseItems(expenseLines.length > 0 ? expandedExpenseLines : []);
            setItemLines(expandedItemLines.length > 0 ? expandedItemLines : []);

            if (fields.customFields) {
                setCustomFieldValuesBulk(
                    fields.customFields.length > 0 ? convertCustomFieldsToState(fields.customFields) : {}
                );
            }
        },
        [
            fieldSettings.dueDate,
            editingContextItems,
            expenseCategories,
            setCustomFieldValuesBulk,
            setExpenseItems,
            setItemLines,
            setNextLineNumber,
            terms,
            updateFieldsBulk,
        ]
    );

    useEffect(() => {
        if (shouldPrefillEditFormRef.current) {
            updateRequestData(request);

            shouldPrefillEditFormRef.current = false;
        }
    }, [request, updateRequestData]);

    useUnmount(() => {
        resetBillFormState();
    });

    const getRequestTransfer = useCallback((): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            currency: currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode,
            amount: totalAmount,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || 1,
            exchangeRateSource:
                typeof fieldValues.exchangeRate === 'number'
                    ? domain.ExchangeRateSource.Manual
                    : domain.ExchangeRateSource.NetSuite,
            exchangeRateDate: isFutureDate(fieldValues.date)
                ? moment.utc().format('yyyy-MM-DD')
                : moment.utc(fieldValues.date).format('yyyy-MM-DD'),
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        const isDraft = request.statusV2 === domain.RequestStatusV2.Draft;

        transfer.netSuiteVendorBillOrderDetails = {
            taxAmount,
            totalAmount,
            discountAmount,
            transactionNumber: fieldValues.transactionNumber || null,
            reference: fieldValues.reference || null,
            vendorId: fieldValues.vendor?.id ?? null,
            currencyId: fieldValues.currency?.id ?? null,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate || null,
            discountDate: discountDate || null,
            isPaymentHold: fieldValues.isPaymentHold || false,
            dueDate: fieldValues.dueDate || null,
            transactionDate: fieldValues.date || null,
            memo: fieldValues.memo || null,
            billingAddressId: fieldValues.address?.id ?? null,
            postingPeriodId: fieldValues.postingPeriod?.id ?? null,
            termId: fieldValues.terms?.id ?? null,
            accountId: fieldValues.account?.id ?? null,
            departmentId: fieldValues.department?.id ?? null,
            classId: fieldValues.class?.id ?? null,
            locationId: fieldValues.location?.id ?? null,
            customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
            ...getExpenseAndItemLineTransfers(domain.IntegrationCode.NetSuiteBill, isDraft),
        };

        return transfer;
    }, [
        taxAmount,
        currencies,
        customFieldsValues,
        defaultCurrencyRate,
        discountAmount,
        discountDate,
        fieldValues,
        pageContext.templateVersion,
        request.attachments,
        request.companyId,
        request.id,
        request.requestNote.value,
        request.statusV2,
        totalAmount,
    ]);

    useEffect(() => {
        setCustomGetRequestTransfer(() => getRequestTransfer);
        setUpdateNetSuiteBillData(() => updateRequestData);
    }, [getRequestTransfer, setCustomGetRequestTransfer, setUpdateNetSuiteBillData, updateRequestData]);

    const requestCurrencyISOCode = useMemo(() => {
        return currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode ?? null;
    }, [currencies, fieldValues.currency]);

    const getValidationErrors = async () => {
        setShowErrors(true);

        return getBillValidationErrors({
            fields: fieldValues,
            customFieldsValues: customFieldsValues,
            customFieldsList: customFields,
            workflowId: request.template.id,
            attachments: request.attachments,
            expenseLines: expenseItems,
            itemLines,
            totalAmount,
            taxAmount,
            discountAmount,
            defaultRate: defaultCurrencyRate,
            fieldSettings,
            expensesFieldSettings,
            attachmentsFieldSettings,
            itemsFieldSettings,
            companyCurrency: request.companyCurrency,
            requestCurrencyISOCode,
        });
    };

    const closeVendorPopup = useCallback(() => {
        setIsVendorPopupOpened(false);
    }, []);

    const onNewVendorSave = useCallback(
        (vendor: CreateNetSuiteVendor['response']['vendor']) => {
            updateFieldsBulk({
                vendor: {
                    id: vendor.id,
                    text: vendor.name,
                },
                vatRegistrationNumber: vendor.taxNumber,
                address: null,
                currency: null,
            });

            closeVendorPopup();
        },
        [updateFieldsBulk, closeVendorPopup]
    );

    const onNewVendorClick = useCallback(() => {
        setIsVendorPopupOpened(true);

        return Promise.resolve(null);
    }, []);

    const showExpensesTable =
        availableLineItemType === domain.NetSuiteAvailableLineItemType.ExpenseLines ||
        availableLineItemType === domain.NetSuiteAvailableLineItemType.All;

    const showItemsTable =
        availableLineItemType === domain.NetSuiteAvailableLineItemType.ItemLines ||
        availableLineItemType === domain.NetSuiteAvailableLineItemType.All;

    const isOcrUploadButtonVisible =
        useIsOcrUploadButtonVisible(company, request) && isFieldVisible(attachmentsFieldSettings.attachments);

    const {
        isBelgradeUIFilesUploadSection,
        isBelgradeUILineItemsTotalSection,
        isBelgradeUINetSuiteVendorCreationPopup,
    } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.toolbarText}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                    />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <NetSuiteBillFieldsSection
                            requestId={request.id}
                            companyId={request.companyId}
                            workflowId={request.template.id}
                            discountAmount={discountAmount}
                            discountDate={discountDate}
                            savedVendorId={request.details.vendor?.id}
                            canCreateVendor={canCreateContact}
                            initialMemoValue={request.details.memo}
                            onNewVendorClick={onNewVendorClick}
                            updateItemsOnCurrencyChange={updateItemsOnCurrencyChange}
                            updateExpenseItemsOnCurrencyChange={updateExpenseItemsOnCurrencyChange}
                            updateExpenseItemsAccount={updateExpenseItemsAccount}
                            showExpensesTable={showExpensesTable}
                        />

                        {showExpensesTable && (
                            <NetSuiteExpenses
                                currency={fieldValues.currency}
                                integrationCode={domain.IntegrationCode.NetSuiteBill}
                                workflowId={request.template.id}
                            />
                        )}

                        {showItemsTable && (
                            <NetSuiteItems
                                currency={fieldValues.currency}
                                integrationCode={domain.IntegrationCode.NetSuiteBill}
                                workflowId={request.template.id}
                            />
                        )}

                        {isBelgradeUILineItemsTotalSection ? (
                            <NetSuiteRequestItemsSummary
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                currency={fieldValues.currency}
                            />
                        ) : (
                            <NetSuiteRequestFooterLegacy
                                netAmount={netAmount}
                                taxAmount={taxAmount}
                                totalAmount={totalAmount}
                                currency={fieldValues.currency}
                            />
                        )}

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <>
                                    <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />

                                    {isOcrUploadButtonVisible && <OcrUploadButton request={request} />}
                                </>
                            ))}
                    </ContentInner>
                </Content>

                {isBelgradeUINetSuiteVendorCreationPopup ? (
                    <Popup open={isVendorPopupOpened} onClose={closeVendorPopup} size='large'>
                        <NetSuiteVendorCreationPopupContent
                            terms={editingContext.terms || []}
                            currencies={editingContext.currencies || []}
                            requestSubsidiary={request.details.subsidiary}
                            onSave={onNewVendorSave}
                        />
                    </Popup>
                ) : (
                    <PopupOld isOpen={isVendorPopupOpened} onRequestClose={closeVendorPopup}>
                        <NetSuiteVendorCreationPopup
                            companyId={request.companyId}
                            terms={editingContext.terms || []}
                            currencies={editingContext.currencies || []}
                            requestSubsidiary={request.details.subsidiary}
                            onSave={onNewVendorSave}
                        />
                    </PopupOld>
                )}
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteBillRequestForm.displayName = 'NetSuiteBillRequestForm';
